<template>
    <div class="sps-help">
        <header class="sps-help__header">
            <button v-if="stack.length || article || query" type="button" class="sps-help__back" :aria-label="$t('shell.help')" @click="back">
                <svg viewBox="0 0 24 24"><path d="M15 5l-7 7 7 7" /></svg>
            </button>
            <span class="sps-help__title">{{ headerTitle }}</span>
            <button type="button" class="sps-help__close" @click="minimize"><svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18" /></svg></button>
        </header>

        <div class="sps-help__body">
            <div v-if="!article" class="sps-help__search">
                <svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
                <input v-model="query" type="search" :placeholder="$t('shell.helpSearch')" @input="onSearch" />
            </div>

            <div v-if="loading" class="sps-help__state">…</div>
            <div v-else-if="error" class="sps-help__state">{{ $t("shell.helpLoadFailed") }}</div>

            <template v-else-if="article">
                <article class="sps-help__article">
                    <h2>{{ nameOf(article) }}</h2>
                    <div class="sps-help__content" v-html="contentOf(article)"></div>
                    <div class="sps-help__reactions">
                        <div class="sps-help__prompt">{{ reacted ? $t("catalog.thanks") : $t("catalog.reactionPrompt") }}</div>
                        <div v-if="!reacted" class="sps-help__emojis">
                            <button type="button" @click="react('sad')">😞</button>
                            <button type="button" @click="react('neutral')">😐</button>
                            <button type="button" @click="react('happy')">😊</button>
                        </div>
                    </div>
                    <button type="button" class="sps-help__cta" @click="startChat">{{ $t("shell.startChat") }}</button>
                </article>
            </template>

            <template v-else>
                <p v-if="results !== null && results.length" class="sps-help__count">{{ $t("shell.searchResultCount", { n: results.length }) }}</p>
                <p v-if="!list.length" class="sps-help__state">{{ $t("shell.helpEmpty") }}</p>
                <section v-if="collections.length" class="sps-help__group">
                    <h3 v-if="articles.length">{{ $t("shell.collections") }}</h3>
                    <button v-for="c in collections" :key="c._id" type="button" class="sps-help__row" @click="openCollection(c)">
                        <span class="sps-help__rowicon"><svg viewBox="0 0 24 24"><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /></svg></span>
                        <span class="sps-help__rowtext">
                            <span class="sps-help__rowname">{{ nameOf(c) }}</span>
                            <span v-if="countOf(c)" class="sps-help__rowsub">{{ countOf(c) }}</span>
                        </span>
                        <span class="sps-help__chevron">›</span>
                    </button>
                </section>
                <section v-if="articles.length" class="sps-help__group">
                    <h3 v-if="collections.length">{{ $t("shell.articles") }}</h3>
                    <button v-for="a in articles" :key="a._id" type="button" class="sps-help__row sps-help__row--article" @click="openArticle(a)">
                        <span class="sps-help__rowicon sps-help__rowicon--doc"><svg viewBox="0 0 24 24"><path d="M7 3h7l5 5v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" /><path d="M14 3v5h5M9 13h6M9 17h6" /></svg></span>
                        <span v-if="results !== null" class="sps-help__rowtext">
                            <span class="sps-help__rowname" v-html="mark(nameOf(a))"></span>
                            <span v-if="contentOf(a)" class="sps-help__rowsub" v-html="mark(cut(contentOf(a)))"></span>
                        </span>
                        <span v-else class="sps-help__rowtext"><span class="sps-help__rowname">{{ nameOf(a) }}</span></span>
                        <span class="sps-help__chevron">›</span>
                    </button>
                </section>
            </template>
        </div>
    </div>
</template>
<script>
import { mapState } from "vuex";
import _ from "lodash";
import { sendMinimizeAction } from "../../../utils/parentWindowConnection";
import { writeUrlState } from "../../../utils/shell/urlState";
import { highlight, snippet } from "../../../utils/shell/highlight";

const REACTIONS_KEY = "hc-reactions";
const readReactions = () => {
    try { return JSON.parse(localStorage.getItem(REACTIONS_KEY) || "{}") || {}; } catch (e) { return {}; }
};

export default {
    name: "ShellHelpView",
    props: { colors: { type: Object, default: () => ({}) } },
    data() {
        return { stack: [], article: null, query: "", results: null, loading: false, error: false, reactions: readReactions() };
    },
    computed: {
        ...mapState({ content: (s) => s.catalog.content, helpCenter: (s) => s.catalog, defaultLocale: (s) => s.catalog.defaultLocale }),
        list() { return this.results !== null ? this.results : this.content; },
        collections() { return this.list.filter((x) => x.type === "menu"); },
        articles() { return this.list.filter((x) => x.type === "content"); },
        headerTitle() {
            if (this.article) return this.nameOf(this.article);
            if (this.stack.length) return this.nameOf(this.stack[this.stack.length - 1]);
            return this.$t("shell.help");
        },
        urlState() {
            const col = this.stack[this.stack.length - 1];
            return { collection: !this.article && col ? col._id : null, article: this.article ? this.article.slug || this.article._id : null };
        },
        reacted() { return this.article ? this.reactions[this.article._id] || null : null; },
    },
    methods: {
        minimize() { sendMinimizeAction(); },
        mark(text) { return highlight(text, this.query); },
        cut(text) { return snippet(text, this.query); },
        lc(item) {
            const map = (item && item.languageContent) || {};
            return map[this.$i18n.locale] || map[this.defaultLocale] || map[Object.keys(map)[0]] || {};
        },
        nameOf(item) { return this.lc(item).name || ""; },
        contentOf(item) { return this.lc(item).content || ""; },
        countOf(c) {
            const n = c.articleCount || 0;
            return n ? this.$t("shell.articleCount", { n }) : "";
        },
        async run(fn) {
            this.loading = true; this.error = false;
            try { await fn(); } catch (e) { this.error = true; } finally { this.loading = false; }
        },
        loadRoot() { return this.run(() => this.$store.dispatch("catalog/ensureRoot")); },
        openCollection(c) {
            this.query = ""; this.results = null;
            this.stack.push(c);
            return this.run(() => this.$store.dispatch("catalog/getChildren", c._id));
        },
        openArticle(a) {
            this.query = ""; this.results = null;
            return this.run(async () => {
                await this.$store.dispatch("catalog/openArticle", { item: a, lang: this.$i18n.locale });
                this.article = this.content[0] || a;
                this.$store.dispatch("catalog/viewArticle", { id: a._id, lang: this.$i18n.locale });
            });
        },
        back() {
            if (this.query || this.results !== null) { this.query = ""; this.results = null; return; }
            if (this.article) {
                this.article = null;
                const parent = this.stack[this.stack.length - 1];
                return parent ? this.run(() => this.$store.dispatch("catalog/getChildren", parent._id)) : this.loadRoot();
            }
            this.stack.pop();
            const parent = this.stack[this.stack.length - 1];
            return parent ? this.run(() => this.$store.dispatch("catalog/getChildren", parent._id)) : this.loadRoot();
        },
        react(reaction) {
            if (!this.article) return;
            this.$set(this.reactions, this.article._id, reaction);
            this.$store.dispatch("catalog/reactArticle", { id: this.article._id, reaction, lang: this.$i18n.locale });
            try { localStorage.setItem(REACTIONS_KEY, JSON.stringify(this.reactions)); } catch (e) { /* depolama kapali */ }
        },
        startChat() {
            if (this.article) this.$store.dispatch("catalog/startChatFromArticle", { id: this.article._id, lang: this.$i18n.locale });
            this.$emit("ask");
        },
        runSearch() {
            const q = this.query.trim();
            if (q.length < 2) { this.results = null; return; }
            this.$store.dispatch("catalog/searchCatalog", { query: q, lang: this.$i18n.locale })
                .then((r) => { this.results = (r.data || []).map((x) => ({ ...x, languageContent: { [this.$i18n.locale]: x.languageContent[0] } })); })
                .catch(() => { this.results = []; });
        },
        async consumePending() {
            // Home'dan gelen makale: kok liste yerine dogrudan makale acilir
            const p = this.$store.state.shell.pendingArticle;
            if (p && p._id) {
                this.$store.dispatch("shell/clearPendingArticle");
                await this.openArticle({ _id: p._id, type: "content", parentId: p.collectionId || null, slug: p.slug || "", languageContent: { [this.$i18n.locale]: { name: p.name || "", content: "" } } });
                this.stack = [];
                return;
            }
            // adresten geri yuklenen koleksiyon
            const colId = this.$store.state.shell.pendingCollection;
            if (colId) {
                this.$store.dispatch("shell/clearPendingCollection");
                let col = null;
                await this.run(async () => { col = await this.$store.dispatch("catalog/openCollectionById", colId); });
                if (col) { this.stack = [col]; return; }
                this.error = false;
                this.loadRoot();
                return;
            }
            const q = this.$store.state.shell.pendingQuery;
            if (q) {
                this.$store.dispatch("shell/setPendingQuery", null);
                await this.loadRoot();
                this.query = q;
                this.runSearch();
                return;
            }
            this.loadRoot();
        },
    },
    watch: {
        // acik koleksiyon ve makale adrese yazilir; yenilemede ayni ekran gelir
        urlState: { handler(v) { writeUrlState(v); }, immediate: true },
        "$store.state.shell.pendingQuery"(q) { if (q) this.$nextTick(() => this.consumePending()); },
        "$store.state.shell.pendingArticle"(a) { if (a) this.$nextTick(() => this.consumePending()); },
        "$store.state.shell.pendingCollection"(c) { if (c) this.$nextTick(() => this.consumePending()); },
    },
    created() {
        // Debounce ornek basina
        this.onSearch = _.debounce(this.runSearch, 300);
    },
    mounted() {
        this.consumePending();
    },
};
</script>
<style lang="scss">
.sps-help { display: flex; flex-direction: column; height: 100%; background: var(--background-color-first, #fff); color: var(--primary-text, #1f2337);
    &__header { flex-shrink: 0; display: flex; align-items: center; gap: 6px; padding: 12px 12px 12px 8px; border-bottom: 1px solid var(--sps-border, rgba(15, 23, 42, 0.08)); }
    &__back, &__close { width: 32px; height: 32px; border: 0; border-radius: 8px; background: transparent; color: inherit; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0;
        svg { width: 18px; height: 18px; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
        &:hover { background: rgba(15, 23, 42, 0.06); } }
    &__close { margin-left: auto; background: rgba(15, 23, 42, 0.06); }
    &__title { flex: 1; min-width: 0; font-weight: 600; font-size: 15px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; text-align: center; }
    &__body { flex: 1; min-height: 0; overflow: auto; padding: 12px 14px 16px; }
    &__search { display: flex; align-items: center; gap: 8px; padding: 0 12px; border: 1px solid var(--sps-border, rgba(15, 23, 42, 0.12)); border-radius: 12px; background: var(--sps-surface, #fff); margin-bottom: 12px;
        svg { width: 18px; height: 18px; fill: none; stroke: currentColor; stroke-width: 1.8; stroke-linecap: round; opacity: 0.5; flex-shrink: 0; }
        input { flex: 1; min-width: 0; border: 0; background: transparent; padding: 11px 0; font: inherit; font-size: 13.5px; color: inherit; outline: none; }
        &:focus-within { border-color: var(--button-color, #4f46e5); box-shadow: 0 0 0 3px color-mix(in srgb, var(--button-color, #4f46e5) 14%, transparent); } }
    &__state { padding: 32px 12px; text-align: center; font-size: 13.5px; color: var(--sps-muted, rgba(15, 23, 42, 0.6)); }
    &__group { margin-bottom: 14px; h3 { margin: 4px 4px 8px; font-size: 11.5px; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; opacity: 0.5; } }
    &__row { display: flex; align-items: center; gap: 12px; width: 100%; padding: 12px 14px; margin-bottom: 8px; border: 1px solid var(--sps-border, rgba(15, 23, 42, 0.07)); border-radius: var(--sps-radius-md, 14px); background: var(--sps-surface, #fff); color: inherit; font: inherit; text-align: left; cursor: pointer; box-shadow: var(--sps-shadow, 0 2px 10px rgba(30, 30, 80, 0.05)); transition: box-shadow 140ms ease, transform 140ms ease;
        &:hover { box-shadow: 0 6px 18px rgba(30, 30, 80, 0.1); }
        &:active { transform: scale(0.995); } }
    &__rowicon { flex-shrink: 0; width: 38px; height: 38px; border-radius: 11px; display: inline-flex; align-items: center; justify-content: center; background: color-mix(in srgb, var(--button-color, #4f46e5) 12%, transparent); color: var(--button-color, #4f46e5);
        svg { width: 20px; height: 20px; fill: none; stroke: currentColor; stroke-width: 1.8; stroke-linecap: round; stroke-linejoin: round; }
        &--doc { background: rgba(15, 23, 42, 0.05); color: var(--primary-text, #1f2337); } }
    &__rowtext { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
    &__rowname { font-size: 14px; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    &__rowsub { font-size: 12px; color: var(--sps-muted, rgba(15, 23, 42, 0.6)); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    &__count { margin: 0 4px 8px; font-size: 12px; color: var(--sps-muted, rgba(15, 23, 42, 0.6)); }
    &__rowname, &__rowsub { mark { background: color-mix(in srgb, var(--button-color, #4f46e5) 18%, transparent); color: inherit; border-radius: 3px; padding: 0 1px; } }
    &__chevron { opacity: 0.35; font-size: 20px; line-height: 1; }
    // genel baslik rengi koyu temada okunmuyordu; basliklar tema metin rengini alir
    &__article { h2 { margin: 4px 0 14px; font-size: 19px; line-height: 1.3; color: inherit; } }
    &__content { font-size: 14.5px; line-height: 1.6; word-break: break-word;
        img { max-width: 100%; border-radius: 10px; } p { margin: 0 0 12px; } h1, h2, h3, h4 { margin: 18px 0 8px; line-height: 1.3; color: inherit; } pre { overflow: auto; padding: 10px 12px; border-radius: 10px; background: var(--sps-border, rgba(15, 23, 42, 0.05)); } a { color: var(--button-color, #4f46e5); } }
    &__reactions { margin: 20px 0 12px; padding: 14px; border-radius: 14px; background: rgba(15, 23, 42, 0.04); text-align: center; }
    &__prompt { font-size: 13px; opacity: 0.75; margin-bottom: 8px; }
    &__emojis { display: flex; justify-content: center; gap: 10px; button { width: 44px; height: 44px; border: 0; border-radius: 50%; background: var(--sps-surface-raised, #fff); font-size: 22px; cursor: pointer; box-shadow: var(--sps-shadow, 0 2px 8px rgba(30, 30, 80, 0.08)); &:hover { transform: scale(1.08); } } }
    &__cta { display: block; width: 100%; padding: 12px; border: 0; border-radius: 999px; background: var(--button-color, #4f46e5); color: var(--button-text-color, #fff); font: inherit; font-size: 14px; font-weight: 600; cursor: pointer; }
}
</style>



// WEBPACK FOOTER //
// src/components/V2/shell/HelpView.vue